/**
 * The upload contract form's rules, kept apart from the dialog so they can be
 * tested: the expiry date it previews, what it accepts, what it tells legal
 * will happen, and the request it sends.
 *
 * The limits mirror the server's (routes/contracts.js upload-signed, and
 * utils/contractRenewals.js parseRenewalSettings), which enforces them anyway.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const isWhole = (value, min, max) => {
  const n = Number(value);
  return String(value).trim() !== '' && Number.isInteger(n) && n >= min && n <= max;
};

/**
 * 'YYYY-MM-DD' plus whole calendar months, clamped to a shorter month's last
 * day — the arithmetic the server stores the end date with, so the preview and
 * the saved contract agree. Empty when there is nothing to add to.
 */
export const addMonthsToDay = (day, months) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day || '') || !isWhole(months, 1, 1200)) return '';
  const [y, m, d] = day.split('-').map(Number);
  const target = new Date(Date.UTC(y, m - 1 + Number(months), 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(d, lastDay));
  return target.toISOString().slice(0, 10);
};

export const formatDay = (day) => {
  if (!day) return '';
  const [y, m, d] = day.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
};

/**
 * What an upload to `contract` is — the same four cases the server's
 * upload-signed route decides between, plus 'terminated', which it refuses.
 */
export const uploadModeFor = (contract) => {
  const status = contract?.status;
  if (status === 'terminated') return 'terminated';
  if (status === 'recalled') return 'correcting';
  if (status === 'expired') return 'reactivate';
  if (contract?.signedContract && ['active', 'termination_recommended'].includes(status)) return 'reissue';
  return 'first';
};

/** Whether an upload in `mode` keeps the contract's dates unless new ones are given. */
export const keepsDates = (mode) => mode === 'correcting' || mode === 'reissue';

/** Field → message for everything that would stop the upload. */
export const validateUploadForm = (values, { keepsDates: keeping = false } = {}) => {
  const errors = {};
  if (!values.file) errors.file = 'Choose the signed contract to upload';

  // A new or reactivated contract needs both dates. A correction or re-issue
  // may leave both blank to keep the dates it has — including any renewals
  // already added to its end date — but one without the other would move only
  // half of them.
  const hasStart = Boolean(values.startDate);
  const hasValidity = String(values.validity || '').trim() !== '';
  const needDates = !keeping || hasStart || hasValidity;
  if (needDates && !hasStart) errors.startDate = 'When does the contract start?';
  if (needDates && !hasValidity) errors.validity = 'How many months the contract runs';
  if (hasValidity && !isWhole(values.validity, 1, 120)) errors.validity = 'A whole number of months, 1 to 120';
  if (String(values.noticePeriod || '').trim() !== '' && !isWhole(values.noticePeriod, 0, 60)) {
    errors.noticePeriod = 'A whole number of months, 0 to 60';
  }
  if (values.autoRenew) {
    if (!isWhole(values.renewalTerm, 1, 120)) errors.renewalTerm = 'A whole number of months, 1 to 120';
    if (!isWhole(values.renewalNotice, 0, 365)) errors.renewalNotice = 'A whole number of days, 0 to 365';
  }
  return errors;
};

/** One sentence saying what happens at the end date, and who hears first. */
export const renewalSummary = (values) => {
  const expiry = addMonthsToDay(values.startDate, values.validity);
  if (!expiry) return '';
  if (!values.autoRenew) return `Expires on ${formatDay(expiry)}. It will not renew unless auto-renew is turned on.`;
  const head = values.department ? `the ${values.department} head` : 'the department head';
  return `Renews for ${values.renewalTerm} months on ${formatDay(expiry)} unless turned off. `
    + `Legal and ${head} are warned ${values.renewalNotice} days before.`;
};

/** The multipart body upload-signed reads. */
export const toFormData = (values, supplierId) => {
  const fd = new FormData();
  if (supplierId) fd.append('supplierId', supplierId);
  fd.append('contract', values.file);
  if (values.startDate) fd.append('startDate', values.startDate);
  if (String(values.validity || '').trim()) fd.append('validityMonths', String(values.validity).trim());
  if (String(values.noticePeriod || '').trim()) fd.append('noticePeriodMonths', String(values.noticePeriod).trim());
  if (values.department) fd.append('department', values.department);
  if (values.comment?.trim()) fd.append('comment', values.comment.trim());
  // Always sent, so turning auto-renew off on a replacement is a decision the
  // server records, not an absent field it ignores.
  fd.append('autoRenew', values.autoRenew ? 'true' : 'false');
  if (values.autoRenew) {
    fd.append('renewalTermMonths', String(values.renewalTerm).trim());
    fd.append('renewalNoticePeriod', String(values.renewalNotice).trim());
  }
  return fd;
};
